import { useCallback, useState } from 'react';
import OrganisationService from 'components/organisation/OrganisationService';
import ProfileService from 'shared/profile/profile.service';
import AuthService from 'core/auth.service';
import RouterService from 'core/router.service';
import useStore from 'store/AccountStore';
import useNotification from 'store/NotificationStore';
import { extractErrorMessage, getScheduleMenuType } from 'utils';

/**
 * Switches the active organisation.
 * - Pages under Schedules ask for confirmation, then reload to the default page.
 * - Every other page switches in place (no dialog, no reload). If the current page is not
 *   allowed for the new organisation, it reloads to the default page instead.
 */
function useSwitchOrganisation() {
  const { user, setUserData } = useStore((state) => state);
  const { danger } = useNotification((state) => state);
  const [isSwitching, setIsSwitching] = useState(false);
  const [pendingOrganisation, setPendingOrganisation] = useState(null);

  // Gets a token for the organisation and stores it so later requests are scoped to it
  const requestOrganisationTokenAsync = useCallback(
    async (organisationId) => {
      try {
        const response = await OrganisationService.switchOrganisation({
          organisationId,
        });
        AuthService.setUserIdentity({
          ...AuthService.getUserIdentity(),
          userId: response.data.userId,
          roleId: response.data.roleId,
          organisationId: response.data.organisationId,
          token: response.data.token,
        });
        return response.data;
      } catch (error) {
        const message = await extractErrorMessage(
          error,
          'Failed to switch organisation',
        );
        danger(message);
        return null;
      }
    },
    [danger],
  );

  const reloadToDefaultPageAsync = useCallback(async (userId) => {
    let landingPath = '/';
    try {
      const response = await ProfileService.getUser(userId);
      const freshUser = response.data;
      landingPath = RouterService.getDefaultLandingPath({
        userPageAccess: freshUser.userPageAccess ?? null,
        roleId: freshUser.roleId,
        schedulesMenuType: getScheduleMenuType(freshUser.schedulesMenuType),
        hasMultipleOrganisation: freshUser.organisations?.length > 1,
      });
    } catch (error) {
      // Fall back to the app root, which re-hydrates the session on load
    }
    window.location.assign(`${process.env.REACT_APP_SUBFOLDER}${landingPath}`);
  }, []);

  const switchAndReloadAsync = useCallback(
    async (organisationId) => {
      if (organisationId === user?.organisationId) return true;

      setIsSwitching(true);
      const data = await requestOrganisationTokenAsync(organisationId);
      if (!data) {
        setIsSwitching(false);
        return false;
      }

      await reloadToDefaultPageAsync(data.userId);
      return true;
    },
    [reloadToDefaultPageAsync, requestOrganisationTokenAsync, user?.organisationId],
  );

  const switchOrganisationInPlaceAsync = useCallback(
    async (organisationId) => {
      if (organisationId === user?.organisationId) return true;

      setIsSwitching(true);
      const data = await requestOrganisationTokenAsync(organisationId);
      if (!data) {
        setIsSwitching(false);
        return false;
      }

      // Use the fresh user, not a merge with the old one, so permissions match the new organisation
      let freshUser;
      try {
        const response = await ProfileService.getUser(data.userId);
        freshUser = response.data;
      } catch (error) {
        setIsSwitching(false);
        await reloadToDefaultPageAsync(data.userId);
        return true;
      }

      const currentPath = RouterService.getRelativePath(window.location.pathname);
      const canStayOnPage = RouterService.isPageAccessible(currentPath, {
        userPageAccess: freshUser.userPageAccess ?? null,
        roleId: freshUser.roleId,
        hasMultipleOrganisation: freshUser.organisations?.length > 1,
      });

      setIsSwitching(false);
      if (!canStayOnPage) {
        await reloadToDefaultPageAsync(data.userId);
        return true;
      }

      setUserData(freshUser);
      return true;
    },
    [
      reloadToDefaultPageAsync,
      requestOrganisationTokenAsync,
      setUserData,
      user?.organisationId,
    ],
  );

  const requestSwitchAsync = useCallback(
    async (organisation) => {
      if (!organisation || organisation.id === user?.organisationId) return true;

      if (RouterService.isSchedulesPath(window.location.pathname)) {
        setPendingOrganisation(organisation);
        return true;
      }
      return switchOrganisationInPlaceAsync(organisation.id);
    },
    [switchOrganisationInPlaceAsync, user?.organisationId],
  );

  const cancelSwitch = useCallback(() => {
    setPendingOrganisation(null);
  }, []);

  const confirmSwitchAsync = useCallback(async () => {
    if (!pendingOrganisation) return false;
    const didSwitch = await switchAndReloadAsync(pendingOrganisation.id);
    if (!didSwitch) setPendingOrganisation(null);
    return didSwitch;
  }, [pendingOrganisation, switchAndReloadAsync]);

  return {
    cancelSwitch,
    confirmSwitchAsync,
    isSwitching,
    pendingOrganisation,
    requestSwitchAsync,
    switchOrganisationInPlaceAsync,
  };
}

export default useSwitchOrganisation;
